import * as React from 'react';
import { RowItems } from '../../models/TableProps';
import { DocumentData } from '../../models/DocumentLibrary';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import { Box, Button, Menu, MenuItem, Typography, styled } from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import { useTranslation } from 'react-i18next';
import { formatBytes } from '../../utils/numberFormatter';
import { useState } from 'react';
import { useAdminDocumentLibrary } from 'hooks/dokumentLibrary/useAdminDocumentLibrary';
import useConfirmDialog from 'hooks/dialogs/useConfirmDialog';
import usePdfDialog from 'hooks/usePdfDialog';

const StyledMenu = styled(Menu)(({ theme }) => ({
    marginTop: '30px',
    '& .MuiMenu-list': {
        padding: 0,
    },
}));

export const AdminDocumentLibraryTableRow: React.FC<RowItems<DocumentData>> = (
    props
) => {
    const { row, index, forceRefresh } = props;

    const { t } = useTranslation('documentLibrary');
    const labelId = `enhanced-table-checkbox-${index}`;

    const { downloadDocument, changeDocumentVisibility, deleteDocument } =
        useAdminDocumentLibrary();

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const confirmDialog = useConfirmDialog();
    const pdfDialog = usePdfDialog();

    const handleDelete = (id: string) => {
        confirmDialog.showDialog({
            title: t('CONFIRM_DELETE.TITLE'),
            content: t('CONFIRM_DELETE.CONTENT'),
            confirmText: t('CONFIRM_DELETE.CONFIRM'),
            cancelText: t('CONFIRM_DELETE.CANCEL'),
            onConfirm: () => {
                deleteDocument(id).then(() => forceRefresh());
            },
        });
    };

    const downloadFile = () => {
        downloadDocument(row.id).then((resp) => {
            const url = window.URL.createObjectURL(new Blob([resp]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', row.name);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
        });
    };

    const openEditDialog = () => {
        pdfDialog.showPdfEditDialog(true, row);
    };

    const handleChangeVisibility = () => {
        if (row.is_active) {
            confirmDialog.showDialog({
                title: t('TOGGLE_ACTIVE.TITLE'),
                content: t('TOGGLE_ACTIVE.CONTENT_HIDDEN'),
                confirmText: t('TOGGLE_ACTIVE.CONFIRM'),
                cancelText: t('TOGGLE_ACTIVE.CANCEL'),
                onConfirm: () => {
                    changeDocumentVisibility(row.id, false).then(() =>
                        forceRefresh()
                    );
                },
            });
        } else {
            confirmDialog.showDialog({
                title: t('TOGGLE_ACTIVE.TITLE'),
                content: t('TOGGLE_ACTIVE.CONTENT_VISIBLE'),
                confirmText: t('TOGGLE_ACTIVE.CONFIRM'),
                cancelText: t('TOGGLE_ACTIVE.CANCEL'),
                onConfirm: () => {
                    changeDocumentVisibility(row.id, true).then(() =>
                        forceRefresh()
                    );
                },
            });
        }
    };

    return (
        <TableRow hover>
            <TableCell
                component="th"
                id={labelId}
                scope="row"
                sx={{ width: '25%' }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        maxInlineSize: '250px',
                        overflowWrap: 'anywhere',
                    }}
                >
                    <PictureAsPdfIcon />
                    <Typography sx={{ paddingLeft: '10px' }}>
                        {row.name}
                    </Typography>
                </Box>
            </TableCell>
            <TableCell sx={{ width: '50%' }}>
                <Typography>{row.description}</Typography>
            </TableCell>
            <TableCell sx={{ width: '5%' }}>
                <Typography>{row.last_modified}</Typography>
            </TableCell>
            <TableCell sx={{ width: '5%' }}>
                <Typography>{formatBytes(row.file_size)}</Typography>
            </TableCell>
            <TableCell sx={{ width: '5%' }}>
                <Typography>
                    {row.is_active ? t('VISIBLE') : t('HIDDEN')}
                </Typography>
            </TableCell>
            <TableCell sx={{ width: '10%' }}>
                <Box sx={{ display: 'flex', flexDirection: 'row', gap: '8px' }}>
                    <Button onClick={handleMenuOpen}>
                        <MoreVertIcon />
                    </Button>
                </Box>
                <StyledMenu
                    id="menu-appbar"
                    anchorEl={anchorEl}
                    anchorOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                    }}
                    open={Boolean(anchorEl)}
                    onClose={handleMenuClose}
                >
                    <MenuItem onClick={downloadFile}>
                        <FileDownloadIcon
                            fontSize="small"
                            sx={{ marginRight: '4px' }}
                        />
                        {t('DOWNLOAD').toString()}
                    </MenuItem>
                    <MenuItem
                        onClick={() => {
                            openEditDialog();
                        }}
                    >
                        <EditIcon
                            fontSize="small"
                            sx={{ marginRight: '4px' }}
                        />
                        {t('EDIT').toString()}
                    </MenuItem>
                    <MenuItem
                        onClick={() => {
                            handleChangeVisibility();
                        }}
                    >
                        <RemoveRedEyeIcon
                            fontSize="small"
                            sx={{ marginRight: '4px' }}
                        />
                        {t('CHANGE_VISIBILITY').toString()}
                    </MenuItem>
                    <MenuItem
                        onClick={() => {
                            handleDelete(row.id);
                        }}
                    >
                        <DeleteForeverIcon
                            fontSize="small"
                            sx={{ marginRight: '4px' }}
                        />
                        {t('DELETE').toString()}
                    </MenuItem>
                </StyledMenu>
            </TableCell>
        </TableRow>
    );
};
